import { motion } from 'framer-motion';
import { HiHandThumbUp, HiHandThumbDown, HiEllipsisVertical } from 'react-icons/hi2';
import { Menu } from '@headlessui/react';

const InterpretationHoverPopup = ({
  interpretation,
  position,
  containerRef,
  onLike,
  onDislike,
  onMouseEnter,
  onMouseLeave,
  user,
  onBanUser,
  onFilterByUser,
  onViewProfile,
  filteredUserId,
  bannedUserIds,
  onViewFullInterpretation,
}) => {
  const title = interpretation.shape_data?.title || 'Untitled';
  const likeCount = interpretation.like_count || 0;
  const dislikeCount = interpretation.dislike_count || 0;
  const userReaction = interpretation.user_reaction;

  const handleLike = (e) => {
    e.stopPropagation();
    if (user) {
      onLike(interpretation.id);
    }
  };

  const handleDislike = (e) => {
    e.stopPropagation();
    if (user) {
      onDislike(interpretation.id);
    }
  };

  const handleBan = () => {
    if (interpretation.user?.id) {
      onBanUser(interpretation.user.id);
    }
  };

  const handleFilter = () => {
    if (interpretation.user?.id) {
      onFilterByUser(interpretation.user.id);
    }
  };

  const handleViewProfile = () => {
    if (interpretation.user?.id) {
      onViewProfile(interpretation.user.id);
    }
  };

  const isBanned = bannedUserIds?.includes(interpretation.user?.id);

  // Calculate responsive position to prevent off-screen popups
  const getResponsiveStyle = () => {
    const isMobile = window.innerWidth < 768;
    const popupWidth = isMobile ? Math.min(280, window.innerWidth - 32) : 320;
    const popupHeight = 220;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const containerRect = containerRef?.current?.getBoundingClientRect();
    
    let left = position.x + 10;
    let top = position.y - 10;
    let transform = 'translateY(-100%)';
    
    // On mobile, use fixed positioning relative to viewport
    if (isMobile) {
      const absoluteX = (containerRect?.left || 0) + position.x;
      const absoluteY = (containerRect?.top || 0) + position.y;
      
      // Adjust horizontal position
      if (absoluteX + popupWidth > viewportWidth - 16) {
        left = viewportWidth - popupWidth - 16;
      } else if (absoluteX < 16) {
        left = 16;
      } else {
        left = absoluteX;
      }
      
      // Adjust vertical position
      if (absoluteY > viewportHeight / 2) {
        top = absoluteY - popupHeight - 10;
        transform = 'translateY(0)';
      } else {
        top = absoluteY + 10;
        transform = 'translateY(0)';
      }
      
      if (top < 16) {
        top = 16;
      }
      if (top + popupHeight > viewportHeight - 16) {
        top = viewportHeight - popupHeight - 16;
      }
      
      return {
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        transform,
        width: `${popupWidth}px`,
        zIndex: 9999,
      };
    } else {
      // Desktop positioning (relative to container)
      if (left + popupWidth > viewportWidth - 20) {
        left = position.x - popupWidth - 10;
      }
      if (left < 20) {
        left = 20;
      }
      
      if (position.y > viewportHeight / 2) {
        transform = 'translateY(-100%)';
      } else {
        transform = 'translateY(10px)';
        top = position.y + 10;
      }
      
      if (top < 20) {
        top = 20;
        transform = 'translateY(0)';
      }
      
      return {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        transform,
        maxWidth: 'min(320px, calc(100vw - 40px))',
        zIndex: 50,
      };
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className="bg-gradient-to-br from-[#111111] via-[#1a1a1a] to-[#0f0f0f] border border-gray-800 rounded-2xl shadow-2xl backdrop-blur-md"
      style={getResponsiveStyle()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="p-3 sm:p-4 w-full">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {interpretation.user?.profile_image_url ? (
              <img
                src={interpretation.user.profile_image_url}
                alt="Profile"
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover shadow-inner flex-shrink-0"
              />
            ) : (
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-700 text-white flex items-center justify-center font-semibold shadow-inner text-xs sm:text-sm flex-shrink-0">
                {interpretation.user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <span className="text-white font-medium tracking-wide text-sm sm:text-base truncate">
              {interpretation.user?.name || 'Anonymous'}
            </span>
          </div>

          <Menu as="div" className="relative inline-block text-left flex-shrink-0">
            <Menu.Button className="text-gray-400 hover:text-white p-1">
              <HiEllipsisVertical className="w-4 h-4 sm:w-5 sm:h-5" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-2 w-48 sm:w-56 origin-top-right bg-[#1a1a1a] border border-gray-700 divide-y divide-gray-700 rounded-xl shadow-xl z-[100] focus:outline-none">
              <div className="p-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleViewProfile}
                      className={`w-full text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors ${
                        active
                          ? 'bg-gray-700/60 backdrop-blur-sm text-blue-400'
                          : 'text-gray-200'
                      }`}
                    >
                      View Profile
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleFilter}
                      className={`w-full text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors ${
                        active
                          ? 'bg-gray-700/60 backdrop-blur-sm text-blue-400'
                          : 'text-gray-200'
                      }`}
                    >
                      {filteredUserId === interpretation.user?.id
                        ? 'Show All Interpretations'
                        : 'View Only This User\'s Interpretations'}
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleBan}
                      className={`w-full text-left px-3 py-2 text-xs sm:text-sm rounded-md transition-colors ${
                        active
                          ? 'bg-gray-700/60 backdrop-blur-sm text-red-400'
                          : 'text-red-300'
                      }`}
                    >
                      {isBanned ? 'Unban This User' : 'Temporarily Hide This User'}
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
        </div>

        <h3 className="font-semibold text-white mb-2 text-sm sm:text-base lg:text-lg tracking-wide line-clamp-2">
          {title}
        </h3>
        <p className="text-gray-400 text-xs sm:text-sm mb-3 leading-relaxed line-clamp-2 sm:line-clamp-3">
          {interpretation.text}
        </p>

        {user && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 px-2 py-1 rounded-full touch-manipulation ${
                userReaction === 'LIKE'
                  ? 'text-blue-400 bg-blue-400/10 border border-blue-400/20'
                  : 'text-gray-400 hover:text-blue-300 hover:bg-blue-300/5 active:bg-blue-300/10'
              }`}
            >
              <HiHandThumbUp className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="min-w-0">{likeCount}</span>
            </button>
            
            <button
              onClick={handleDislike}
              className={`flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 px-2 py-1 rounded-full touch-manipulation ${
                userReaction === 'DISLIKE'
                  ? 'text-red-400 bg-red-400/10 border border-red-400/20'
                  : 'text-gray-400 hover:text-red-300 hover:bg-red-300/5 active:bg-red-300/10'
              }`}
            >
              <HiHandThumbDown className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="min-w-0">{dislikeCount}</span>
            </button>

            <button
              onClick={() => onViewFullInterpretation(interpretation?.id)}
              className="flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 px-2 py-1 rounded-full text-blue-400 bg-blue-400/10 border border-blue-400/20 hover:bg-blue-400/20 hover:text-blue-300 active:bg-blue-400/30 touch-manipulation whitespace-nowrap"
            >
              View Full
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default InterpretationHoverPopup;